<template>
  <div class="page-content container-fluid">
    <div class="row">
      <div class="col-12">
        <h5 class="text-uppercase">Deleted presentation(s)</h5>

        <div class="card">
          <div class="card-body table-content">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0">Deleted Presentations</h6>
              <button 
                v-if="presentations.length > 0"
                class="btn btn-danger btn-sm"
                :disabled="isUnlinkingAll"
                @click="showUnlinkAllModal"
              >
                <i v-if="isUnlinkingAll" class="fa fa-spinner fa-spin mr-1"></i>
                <i v-else class="fas fa-unlink mr-1"></i>
                Unlink All Presentations
              </button>
            </div>
            <table class="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th class="w-300px">Name</th>
                  <th>Access code</th>
                  <th>Unique access code</th>
                  <th>Created date</th>
                  <th>Last modified date</th>
                  <th>Deleted date</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody v-if="!isPresentationLoading && presentations.length !== 0">
                <PresentationRow
                  v-for="presentation in presentations"
                  :key="presentation.id"
                  :presentation="presentation"
                  :user="user"
                  :handleRecoverPresentation="handleRecoverPresentation"
                  @presentation-unlinked="handlePresentationUnlinked"
                />
              </tbody>
            </table>

            <div
              v-if="isPresentationLoading"
              class="d-flex justify-content-center"
            >
              Loading...
            </div>

            <div
              v-if="!isPresentationLoading && presentations.length === 0"
            >
              <h5
                class="text-center font-medium"
                style="line-height: 35px;"
              >
                <span>
                  User doesn't have any deleted presentation yet.
                </span>
              </h5>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Unlink All Confirmation Modal -->
    <b-modal
      id="unlink-all-modal"
      ref="unlink-all-modal"
      hide-footer
      title="Confirm Unlink All Presentations"
    >
      <div class="text-center">
        <div class="mb-3">
          <i class="fas fa-exclamation-triangle text-warning" style="font-size: 3rem;"></i>
        </div>
        <h5 class="mb-3">Are you sure you want to unlink ALL presentations?</h5>
        <p class="text-muted mb-4">
          This will unlink <strong>{{ presentations.length }}</strong> presentation(s) from user 
          <strong>{{ user.email }}</strong>. This action cannot be undone.
        </p>
        <div class="d-flex justify-content-center">
          <button 
            type="button" 
            class="btn btn-danger mr-2" 
            :disabled="isUnlinkingAll"
            @click="confirmUnlinkAll"
          >
            <i v-if="isUnlinkingAll" class="fa fa-spinner fa-spin mr-1"></i>
            <i v-else class="fas fa-unlink mr-1"></i>
            Yes, Unlink All
          </button>
          <button 
            type="button" 
            class="btn btn-secondary" 
            :disabled="isUnlinkingAll"
            @click="hideUnlinkAllModal"
          >
            Cancel
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import configs from '../../config'

// component
import PresentationRow from './PresentationRow.vue'

export default {
  components: {
    PresentationRow,
  },
  data() {
    return {
      presentations: [],
      isPresentationLoading: false,
      isUnlinkingAll: false,
    }
  },
  props: {
    userId: {
      type: Number,
      required: true,
    },
    user: {
      type: Object,
      required: true,
      default: () => {},
    }
  },
  async mounted() {
    this.isPresentationLoading = true
    const res = await this.$http.get(configs.api.getDeletedPresentations, {
      params: {
        userId: this.userId,
        limit: 100,
      },
    })
    this.presentations = res.body.result
    this.isPresentationLoading = false
  },
  methods: {
    ...mapActions('presentations', ['unlinkPresentations']),
    handleRecoverPresentation(presentationId) {
      this.presentations = this.presentations.filter((item) => item.id !== presentationId)
    },
    handlePresentationUnlinked(presentationId) {
      // Remove the unlinked presentation from the list
      this.presentations = this.presentations.filter((item) => item.id !== presentationId)
    },
    showUnlinkAllModal() {
      this.$refs['unlink-all-modal'].show()
    },
    hideUnlinkAllModal() {
      this.$refs['unlink-all-modal'].hide()
    },
    async confirmUnlinkAll() {
      if (this.isUnlinkingAll || this.presentations.length === 0) return

      try {
        this.isUnlinkingAll = true
        
        // Get all presentation IDs
        const presentationIds = this.presentations.map(p => p.id)
        
        // Call the unlink action
        await this.unlinkPresentations({
          // null mean all presentations
          presentationIds: null,
          email: this.user.email,
          userId: this.user.id,
          options: {}
        })
        
        // Clear all presentations from the list
        this.presentations = []
        
        // Show success message
        this.$toasted.success(`Successfully unlinked ${presentationIds.length} presentation(s) from user`)
        
        // Hide the modal
        this.hideUnlinkAllModal()
        
      } catch (error) {
        console.error('Error unlinking all presentations:', error)
        this.$toasted.error('Failed to unlink all presentations. Please try again.')
      } finally {
        this.isUnlinkingAll = false
      }
    },
  },
}
</script>
